const WORKLET_SRC = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const chs = inputs[0];
    if (!chs || !chs.length || !chs[0] || !chs[0].length) return true;
    const n = chs[0].length;
    const nch = chs.length;
    const out = new Float32Array(n);
    if (nch === 1) {
      out.set(chs[0]);
    } else {
      for (let i = 0; i < n; i++) {
        let s = 0;
        for (let c = 0; c < nch; c++) s += chs[c][i];
        out[i] = s / nch;
      }
    }
    this.port.postMessage(out, [out.buffer]);
    return true;
  }
}
registerProcessor("pcm-capture", PcmCaptureProcessor);
`;

/** Android WebView AudioWorklet is often silent or heavily buffered. */
export function preferScriptProcessorCapture(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /DashcamApp/i.test(ua) || /; wv\)/.test(ua);
}

function workletModuleUrl(): string {
  return URL.createObjectURL(new Blob([WORKLET_SRC], { type: "application/javascript" }));
}

let cachedWorkletUrl: string | null = null;
const workletReady = new WeakSet<AudioContext>();

async function ensureCaptureWorklet(ctx: AudioContext): Promise<void> {
  if (workletReady.has(ctx)) return;
  if (!cachedWorkletUrl) cachedWorkletUrl = workletModuleUrl();
  await ctx.audioWorklet.addModule(cachedWorkletUrl);
  workletReady.add(ctx);
}

/** Native hardware rate so our G.711 resampler runs, not Chrome's 8 kHz mixer. */
export function createTalkAudioContext(): AudioContext | null {
  try {
    return new AudioContext({ latencyHint: "interactive" });
  } catch {
    try {
      return new AudioContext();
    } catch {
      return null;
    }
  }
}

export type PcmCapture = {
  stop: () => void;
  mode: "worklet" | "script";
  sampleRate: number;
  blockSize: number;
};

function scriptCapture(
  ctx: AudioContext,
  source: MediaStreamAudioSourceNode,
  silent: GainNode,
  stream: MediaStream,
  onPcm: (samples: Float32Array, sampleRate: number) => void,
): PcmCapture {
  const Ctor = (
    ctx as AudioContext & {
      createScriptProcessor?: (bs: number, i: number, o: number) => ScriptProcessorNode;
    }
  ).createScriptProcessor;
  if (!Ctor) {
    source.disconnect();
    silent.disconnect();
    stream.getTracks().forEach((t) => t.stop());
    throw new Error("Audio capture is not supported in this browser.");
  }
  let proc: ScriptProcessorNode | null = null;
  let block = 256;
  for (const size of [256, 512, 1024]) {
    try {
      proc = Ctor.call(ctx, size, 1, 1);
      block = size;
      break;
    } catch {
      /* some WebViews reject tiny ScriptProcessor sizes */
    }
  }
  if (!proc) {
    source.disconnect();
    silent.disconnect();
    stream.getTracks().forEach((t) => t.stop());
    throw new Error("Audio capture is not supported in this browser.");
  }
  let stopped = false;
  proc.onaudioprocess = (ev) => {
    if (stopped) return;
    onPcm(ev.inputBuffer.getChannelData(0).slice(), ctx.sampleRate);
  };
  source.connect(proc);
  proc.connect(silent);
  silent.connect(ctx.destination);
  const node = proc;
  return {
    mode: "script",
    sampleRate: ctx.sampleRate,
    blockSize: block,
    stop() {
      if (stopped) return;
      stopped = true;
      node.onaudioprocess = null;
      node.disconnect();
      source.disconnect();
      silent.disconnect();
      stream.getTracks().forEach((t) => t.stop());
      if (ctx.state !== "closed") void ctx.close();
    },
  };
}

/** Capture mono PCM. Android WebView uses ScriptProcessor (256); others try AudioWorklet. */
export async function startPcmCapture(
  stream: MediaStream,
  onPcm: (samples: Float32Array, sampleRate: number) => void,
  existingCtx?: AudioContext | null,
): Promise<PcmCapture> {
  const ctx = existingCtx ?? createTalkAudioContext() ?? new AudioContext();
  await ctx.resume();
  const source = ctx.createMediaStreamSource(stream);
  const silent = ctx.createGain();
  silent.gain.value = 0;

  if (preferScriptProcessorCapture()) {
    return scriptCapture(ctx, source, silent, stream, onPcm);
  }

  try {
    await ensureCaptureWorklet(ctx);
    const node = new AudioWorkletNode(ctx, "pcm-capture");
    let stopped = false;
    node.port.onmessage = (ev) => {
      if (stopped) return;
      const samples = ev.data as Float32Array;
      if (samples?.length) onPcm(samples, ctx.sampleRate);
    };
    source.connect(node);
    node.connect(silent);
    silent.connect(ctx.destination);
    return {
      mode: "worklet",
      sampleRate: ctx.sampleRate,
      blockSize: 128,
      stop() {
        if (stopped) return;
        stopped = true;
        node.port.onmessage = null;
        node.disconnect();
        source.disconnect();
        silent.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        if (ctx.state !== "closed") void ctx.close();
      },
    };
  } catch {
    return scriptCapture(ctx, source, silent, stream, onPcm);
  }
}
